import { normalizeLocale, type SupportedLocale } from './config';

export type MicrophonePermissionCopy = {
  title: string;
  callPrompt: string;
  accessLink: string;
  requestDescription: string;
  deniedDescription: string;
  webDeniedDescription: string;
  restrictedDescription: string;
  settingsButton: string;
  cancelButton: string;
  restartHint: string;
};

const COPY: Record<SupportedLocale, MicrophonePermissionCopy> = {
  en: {
    callPrompt: 'Allow {access} to start a voice call',
    accessLink: 'microphone access',
    title: 'Microphone access',
    requestDescription:
      'IMAI uses your microphone for voice calls and voice input.',
    deniedDescription:
      'Microphone access is turned off. Enable it in your system settings, then try again.',
    webDeniedDescription:
      'Microphone access is blocked. Allow it in your browser’s site permissions and check your system’s microphone settings, then try again.',
    restrictedDescription:
      'Microphone access is restricted by your system or administrator.',
    settingsButton: 'Open microphone settings',
    cancelButton: 'Cancel',
    restartHint:
      'You may need to restart IMAI after changing microphone permissions.',
  },
  ar: {
    callPrompt: 'اسمح بـ{access} لبدء مكالمة صوتية',
    accessLink: 'الوصول إلى الميكروفون',
    title: 'الوصول إلى الميكروفون',
    requestDescription:
      'يستخدم IMAI الميكروفون للمكالمات الصوتية والإدخال الصوتي.',
    deniedDescription:
      'الوصول إلى الميكروفون متوقف. فعّله في إعدادات النظام، ثم حاول مرة أخرى.',
    webDeniedDescription:
      'الوصول إلى الميكروفون محظور. اسمح به في أذونات الموقع بالمتصفح وتحقّق من إعدادات الميكروفون في النظام، ثم حاول مرة أخرى.',
    restrictedDescription:
      'الوصول إلى الميكروفون مقيّد بواسطة النظام أو المسؤول.',
    settingsButton: 'فتح إعدادات الميكروفون',
    cancelButton: 'إلغاء',
    restartHint: 'قد تحتاج إلى إعادة تشغيل IMAI بعد تغيير أذونات الميكروفون.',
  },
  es: {
    callPrompt: 'Permite el {access} para iniciar una llamada de voz',
    accessLink: 'acceso al micrófono',
    title: 'Acceso al micrófono',
    requestDescription:
      'IMAI usa tu micrófono para las llamadas de voz y la entrada de voz.',
    deniedDescription:
      'El acceso al micrófono está desactivado. Actívalo en los ajustes del sistema y vuelve a intentarlo.',
    webDeniedDescription:
      'El acceso al micrófono está bloqueado. Permítelo en los permisos del sitio de tu navegador y revisa los ajustes del micrófono del sistema. Luego vuelve a intentarlo.',
    restrictedDescription:
      'El sistema o el administrador ha restringido el acceso al micrófono.',
    settingsButton: 'Abrir ajustes del micrófono',
    cancelButton: 'Cancelar',
    restartHint:
      'Puede que tengas que reiniciar IMAI después de cambiar los permisos del micrófono.',
  },
  'pt-BR': {
    callPrompt: 'Permita o {access} para iniciar uma chamada de voz',
    accessLink: 'acesso ao microfone',
    title: 'Acesso ao microfone',
    requestDescription:
      'O IMAI usa seu microfone para chamadas de voz e entrada de voz.',
    deniedDescription:
      'O acesso ao microfone está desativado. Ative-o nas configurações do sistema e tente novamente.',
    webDeniedDescription:
      'O acesso ao microfone está bloqueado. Permita-o nas permissões do site no navegador e verifique as configurações de microfone do sistema. Depois, tente novamente.',
    restrictedDescription:
      'O acesso ao microfone está restrito pelo sistema ou pelo administrador.',
    settingsButton: 'Abrir configurações do microfone',
    cancelButton: 'Cancelar',
    restartHint:
      'Talvez seja necessário reiniciar o IMAI após alterar as permissões do microfone.',
  },
  fr: {
    callPrompt: 'Autorisez {access} pour démarrer un appel vocal',
    accessLink: 'l’accès au microphone',
    title: 'Accès au microphone',
    requestDescription:
      'IMAI utilise votre microphone pour les appels vocaux et la saisie vocale.',
    deniedDescription:
      'L’accès au microphone est désactivé. Activez-le dans les réglages du système, puis réessayez.',
    webDeniedDescription:
      'L’accès au microphone est bloqué. Autorisez-le dans les permissions du site de votre navigateur et vérifiez les réglages du microphone dans votre système, puis réessayez.',
    restrictedDescription:
      'L’accès au microphone est restreint par votre système ou votre administrateur.',
    settingsButton: 'Ouvrir les réglages du microphone',
    cancelButton: 'Annuler',
    restartHint:
      'Vous devrez peut-être redémarrer IMAI après avoir modifié les autorisations du microphone.',
  },
  it: {
    callPrompt: 'Consenti {access} per avviare una chiamata vocale',
    accessLink: 'l’accesso al microfono',
    title: 'Accesso al microfono',
    requestDescription:
      'IMAI usa il microfono per le chiamate vocali e l’inserimento vocale.',
    deniedDescription:
      'L’accesso al microfono è disattivato. Attivalo nelle impostazioni di sistema, poi riprova.',
    webDeniedDescription:
      'L’accesso al microfono è bloccato. Consentilo nelle autorizzazioni del sito nel browser e controlla le impostazioni del microfono nel sistema, poi riprova.',
    restrictedDescription:
      'L’accesso al microfono è limitato dal sistema o dall’amministratore.',
    settingsButton: 'Apri impostazioni del microfono',
    cancelButton: 'Annulla',
    restartHint:
      'Potrebbe essere necessario riavviare IMAI dopo aver modificato le autorizzazioni del microfono.',
  },
  ko: {
    callPrompt: '음성 통화를 시작하려면 {access}을 허용하세요',
    accessLink: '마이크 접근',
    title: '마이크 접근',
    requestDescription: 'IMAI는 음성 통화와 음성 입력에 마이크를 사용합니다.',
    deniedDescription:
      '마이크 접근이 꺼져 있습니다. 시스템 설정에서 켠 후 다시 시도하세요.',
    webDeniedDescription:
      '마이크 접근이 차단되어 있습니다. 브라우저의 사이트 권한에서 허용하고 시스템의 마이크 설정을 확인한 후 다시 시도하세요.',
    restrictedDescription: '시스템 또는 관리자가 마이크 접근을 제한했습니다.',
    settingsButton: '마이크 설정 열기',
    cancelButton: '취소',
    restartHint: '마이크 권한을 변경한 후 IMAI를 다시 시작해야 할 수 있습니다.',
  },
  de: {
    callPrompt: 'Erlaube {access}, um einen Sprachanruf zu starten',
    accessLink: 'Mikrofonzugriff',
    title: 'Mikrofonzugriff',
    requestDescription:
      'IMAI verwendet dein Mikrofon für Sprachanrufe und Spracheingabe.',
    deniedDescription:
      'Der Mikrofonzugriff ist deaktiviert. Aktiviere ihn in den Systemeinstellungen und versuche es erneut.',
    webDeniedDescription:
      'Der Mikrofonzugriff ist blockiert. Erlaube ihn in den Website-Berechtigungen deines Browsers und prüfe die Mikrofoneinstellungen deines Systems. Versuche es danach erneut.',
    restrictedDescription:
      'Der Mikrofonzugriff ist durch dein System oder die Administration eingeschränkt.',
    settingsButton: 'Mikrofoneinstellungen öffnen',
    cancelButton: 'Abbrechen',
    restartHint:
      'Möglicherweise musst du IMAI nach dem Ändern der Mikrofonberechtigungen neu starten.',
  },
  ja: {
    callPrompt: '音声通話を開始するには{access}を許可してください',
    accessLink: 'マイクへのアクセス',
    title: 'マイクへのアクセス',
    requestDescription: 'IMAIは音声通話と音声入力にマイクを使用します。',
    deniedDescription:
      'マイクへのアクセスがオフになっています。システム設定で有効にしてから、もう一度お試しください。',
    webDeniedDescription:
      'マイクへのアクセスがブロックされています。ブラウザのサイト権限で許可し、システムのマイク設定を確認してから、もう一度お試しください。',
    restrictedDescription:
      'システムまたは管理者によってマイクへのアクセスが制限されています。',
    settingsButton: 'マイク設定を開く',
    cancelButton: 'キャンセル',
    restartHint:
      'マイクのアクセス許可を変更した後、IMAIの再起動が必要な場合があります。',
  },
  hi: {
    callPrompt: 'वॉइस कॉल शुरू करने के लिए {access} की अनुमति दें',
    accessLink: 'माइक्रोफ़ोन ऐक्सेस',
    title: 'माइक्रोफ़ोन का ऐक्सेस',
    requestDescription:
      'IMAI वॉइस कॉल और वॉइस इनपुट के लिए आपके माइक्रोफ़ोन का इस्तेमाल करता है।',
    deniedDescription:
      'माइक्रोफ़ोन का ऐक्सेस बंद है। सिस्टम सेटिंग में इसे चालू करें, फिर दोबारा कोशिश करें।',
    webDeniedDescription:
      'माइक्रोफ़ोन का ऐक्सेस ब्लॉक है। ब्राउज़र में साइट की अनुमतियों से इसे चालू करें और सिस्टम की माइक्रोफ़ोन सेटिंग जाँचें, फिर दोबारा कोशिश करें।',
    restrictedDescription:
      'आपके सिस्टम या एडमिन ने माइक्रोफ़ोन का ऐक्सेस सीमित किया है।',
    settingsButton: 'माइक्रोफ़ोन सेटिंग खोलें',
    cancelButton: 'रद्द करें',
    restartHint:
      'माइक्रोफ़ोन की अनुमतियाँ बदलने के बाद आपको IMAI फिर से शुरू करना पड़ सकता है।',
  },
  ru: {
    callPrompt: 'Разрешите {access}, чтобы начать голосовой звонок',
    accessLink: 'доступ к микрофону',
    title: 'Доступ к микрофону',
    requestDescription:
      'IMAI использует микрофон для голосовых звонков и голосового ввода.',
    deniedDescription:
      'Доступ к микрофону отключён. Включите его в настройках системы и повторите попытку.',
    webDeniedDescription:
      'Доступ к микрофону заблокирован. Разрешите его в настройках доступа сайта в браузере и проверьте настройки микрофона в системе, затем повторите попытку.',
    restrictedDescription:
      'Доступ к микрофону ограничен системой или администратором.',
    settingsButton: 'Открыть настройки микрофона',
    cancelButton: 'Отмена',
    restartHint:
      'После изменения разрешений микрофона может потребоваться перезапустить IMAI.',
  },
  zh: {
    callPrompt: '请允许{access}以开始语音通话',
    accessLink: '麦克风访问权限',
    title: '麦克风访问权限',
    requestDescription: 'IMAI 使用麦克风进行语音通话和语音输入。',
    deniedDescription: '麦克风访问权限已关闭。请在系统设置中开启，然后重试。',
    webDeniedDescription:
      '麦克风访问权限已被阻止。请在浏览器的网站权限中允许访问，并检查系统的麦克风设置，然后重试。',
    restrictedDescription: '系统或管理员限制了麦克风访问权限。',
    settingsButton: '打开麦克风设置',
    cancelButton: '取消',
    restartHint: '更改麦克风权限后，可能需要重启 IMAI。',
  },
  'zh-TW': {
    callPrompt: '請允許{access}以開始語音通話',
    accessLink: '麥克風存取權限',
    title: '麥克風存取權限',
    requestDescription: 'IMAI 使用麥克風進行語音通話和語音輸入。',
    deniedDescription:
      '麥克風存取權限已關閉。請在系統設定中開啟，然後再試一次。',
    webDeniedDescription:
      '麥克風存取權限遭到封鎖。請在瀏覽器的網站權限中允許存取，並檢查系統的麥克風設定，然後再試一次。',
    restrictedDescription: '系統或管理員限制了麥克風存取權限。',
    settingsButton: '開啟麥克風設定',
    cancelButton: '取消',
    restartHint: '變更麥克風權限後，可能需要重新啟動 IMAI。',
  },
  vi: {
    callPrompt: 'Cho phép {access} để bắt đầu cuộc gọi thoại',
    accessLink: 'truy cập micrô',
    title: 'Quyền truy cập micrô',
    requestDescription:
      'IMAI sử dụng micrô cho cuộc gọi thoại và nhập liệu bằng giọng nói.',
    deniedDescription:
      'Quyền truy cập micrô đang tắt. Hãy bật trong phần cài đặt hệ thống rồi thử lại.',
    webDeniedDescription:
      'Quyền truy cập micrô bị chặn. Hãy cho phép trong phần quyền của trang web trên trình duyệt và kiểm tra cài đặt micrô của hệ thống, rồi thử lại.',
    restrictedDescription:
      'Quyền truy cập micrô bị hệ thống hoặc quản trị viên hạn chế.',
    settingsButton: 'Mở cài đặt micrô',
    cancelButton: 'Hủy',
    restartHint:
      'Bạn có thể cần khởi động lại IMAI sau khi thay đổi quyền truy cập micrô.',
  },
  id: {
    callPrompt: 'Izinkan {access} untuk memulai panggilan suara',
    accessLink: 'akses mikrofon',
    title: 'Akses mikrofon',
    requestDescription:
      'IMAI menggunakan mikrofon untuk panggilan suara dan masukan suara.',
    deniedDescription:
      'Akses mikrofon dinonaktifkan. Aktifkan di pengaturan sistem, lalu coba lagi.',
    webDeniedDescription:
      'Akses mikrofon diblokir. Izinkan di pengaturan izin situs pada browser dan periksa pengaturan mikrofon sistem, lalu coba lagi.',
    restrictedDescription:
      'Akses mikrofon dibatasi oleh sistem atau administrator Anda.',
    settingsButton: 'Buka pengaturan mikrofon',
    cancelButton: 'Batal',
    restartHint:
      'Anda mungkin perlu memulai ulang IMAI setelah mengubah izin mikrofon.',
  },
};

export function getMicrophonePermissionCopy(
  locale: string | null | undefined
): MicrophonePermissionCopy {
  return COPY[normalizeLocale(locale)];
}
